/**
 * Dashboard Page - T139, T144, T145
 *
 * Main dashboard with Bento Grid layout showing key metrics
 */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { LoadingSpinner } from '../components/ui';
import { MetricCard } from '../components/dashboard/MetricCard';
import { RevenueChart } from '../components/dashboard/RevenueChart';
import { OutstandingInvoicesWidget } from '../components/dashboard/OutstandingInvoicesWidget';
import { RecentActivityWidget } from '../components/dashboard/RecentActivityWidget';
import { OverdueInvoicesBanner } from '../components/invoices/OverdueInvoicesBanner';
import { useDashboardMetrics, useRevenueByPeriod } from '../hooks/useDashboard';
import { useExpenseStats } from '../services/api/expenses';
import { useSession } from '../hooks/useTenantContext';
import { FilterPill, PageHeader } from '../components/ui';
import { useState } from 'react';

type MetricPeriod = 'month' | 'quarter' | 'year';

function currentQuartal(): string {
  const now = new Date();
  return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
}

/** Monate des laufenden Quartals als 'YYYY-MM'. */
function currentQuarterPeriods(): string[] {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3);
  return [0, 1, 2].map(
    (i) => `${now.getFullYear()}-${String(q * 3 + i + 1).padStart(2, '0')}`
  );
}

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
});

function DashboardPage() {
  const navigate = useNavigate();
  const { data: metrics, isLoading, error } = useDashboardMetrics();
  // Codex-006: tenant-scoped queries gate on `enabled: tenantId !== null`, so
  // on a hard reload they stay disabled (isLoading=false) until /session
  // resolves — without this the cards would flash zeros. Treat session-load
  // as loading.
  const { isLoading: sessionLoading } = useSession();
  // D4 — Zeitraum-Umschalter für die Umsatz-Metrik. Quartal wird client-
  // seitig aus der (für den Chart ohnehin geladenen) Monats-Serie summiert.
  const [period, setPeriod] = useState<MetricPeriod>('month');
  const { data: revenueSeries } = useRevenueByPeriod(12);
  // D1 — Ausgaben-Row aus dem server-aggregierten Stats-Endpoint.
  const quartal = currentQuartal();
  const { data: expenseStats } = useExpenseStats(quartal);

  if (isLoading || sessionLoading) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  if (error) {
    return (
      <ProtectedRoute>
        <AppShell>
          <div className="text-center py-12">
            <p className="text-red-400 mb-4">Fehler beim Laden des Dashboards</p>
          </div>
        </AppShell>
      </ProtectedRoute>
    );
  }

  // Calculate trend
  const monthlyTrend = metrics
    ? metrics.revenueThisMonth - metrics.revenueLastMonth
    : 0;

  const quarterRevenue = (() => {
    const months = new Set(currentQuarterPeriods());
    return (revenueSeries ?? [])
      .filter((r) => months.has(r.period))
      .reduce((sum, r) => sum + r.revenue, 0);
  })();

  const revenueCard: Record<MetricPeriod, { title: string; value: number }> = {
    month: { title: 'Umsatz diesen Monat', value: metrics?.revenueThisMonth || 0 },
    quarter: { title: `Umsatz ${quartal.replace('-', ' ')}`, value: quarterRevenue },
    year: { title: 'Umsatz dieses Jahr', value: metrics?.revenueThisYear || 0 },
  };

  return (
    <ProtectedRoute>
      <AppShell>
        <PageHeader title="Dashboard" subtitle="Übersicht Ihrer Geschäftszahlen">
          <span className="w-full text-xs text-white/50" id="dashboard-period-scope">
            Zeitraum der Umsatzkarte
          </span>
          {/* D4 — Zeitraum-Umschalter */}
          {(['month', 'quarter', 'year'] as const).map((pKey) => (
            <FilterPill
              key={pKey}
              active={period === pKey}
              onClick={() => setPeriod(pKey)}
              data-testid={`dashboard-period-${pKey}`}
              aria-describedby="dashboard-period-scope"
            >
              {pKey === 'month' ? 'Monat' : pKey === 'quarter' ? 'Quartal' : 'Jahr'}
            </FilterPill>
          ))}
        </PageHeader>

        {/* Overdue banner */}
        <OverdueInvoicesBanner />

        {/* Metrics Grid (Bento Grid) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
          {/* Revenue for the selected period (D4) */}
          <MetricCard
            title={revenueCard[period].title}
            value={revenueCard[period].value}
            format="currency"
            onClick={() => navigate({ to: '/invoices', search: { status: 'paid' } })}
            trend={
              period === 'month' && monthlyTrend !== 0
                ? {
                    value: monthlyTrend,
                    label: 'ggü. Vormonat',
                  }
                : undefined
            }
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />

          {/* Revenue this year */}
          <MetricCard
            title="Umsatz dieses Jahr"
            value={metrics?.revenueThisYear || 0}
            format="currency"
            onClick={() => navigate({ to: '/invoices', search: { status: 'paid' } })}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            }
          />

          {/* Outstanding balance (T144 - clickable) */}
          <MetricCard
            title="Offene Forderungen"
            value={metrics?.outstandingBalance || 0}
            format="currency"
            onClick={() => navigate({ to: '/invoices', search: { status: 'open' } })}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            }
          />

          {/* Overdue balance (T144 - clickable) */}
          <MetricCard
            title="Überfällig"
            value={metrics?.overdueBalance || 0}
            format="currency"
            onClick={() => navigate({ to: '/invoices', search: { status: 'overdue' } })}
            className={metrics?.overdueBalance ? 'border-red-500/20' : ''}
            icon={
              <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            }
          />
        </div>

        {/* Second row - Invoice counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-8">
          <MetricCard
            title="Entwürfe"
            value={metrics?.invoicesDraft || 0}
            onClick={() => navigate({ to: '/invoices', search: { status: 'draft' } })}
          />
          <MetricCard
            title="Gesendet"
            value={metrics?.invoicesSent || 0}
            onClick={() => navigate({ to: '/invoices', search: { status: 'sent' } })}
          />
          <MetricCard
            title="Bezahlt"
            value={metrics?.invoicesPaid || 0}
            onClick={() => navigate({ to: '/invoices', search: { status: 'paid' } })}
          />
          <MetricCard
            title="Kunden"
            value={metrics?.totalCustomers || 0}
            onClick={() => navigate({ to: '/customers' })}
          />
        </div>

        {/* D1 — Ausgaben-Row (server-aggregiert via /expenses/stats) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8" data-testid="dashboard-expenses-row">
          <MetricCard
            title={`Ausgaben ${quartal.replace('-', ' ')}`}
            value={expenseStats?.quartalGrossTotal || 0}
            format="currency"
            // Codex-006: card total is status IN ('neu','geprüft'); carry that
            // filter into the drill-down so the list reconciles with the card.
            onClick={() => navigate({ to: '/expenses', search: { quartal, status: ['neu', 'geprüft'] } })}
            icon={
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
            }
          />
          <MetricCard
            title="Belege neu"
            value={expenseStats?.statusNeu || 0}
            onClick={() => navigate({ to: '/expenses', search: { status: 'neu' } })}
          />
          <MetricCard
            title="Rückfragen offen"
            value={expenseStats?.statusRueckfrage || 0}
            onClick={() => navigate({ to: '/expenses', search: { status: 'rückfrage_offen' } })}
            className={expenseStats?.statusRueckfrage ? 'border-amber-500/20' : ''}
          />
        </div>

        {/* Charts and Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Revenue chart - spans 2 columns */}
          <RevenueChart months={12} className="lg:col-span-2" />

          {/* Outstanding invoices */}
          <OutstandingInvoicesWidget limit={5} />
        </div>

        {/* Recent Activity */}
        <div className="mt-6">
          <RecentActivityWidget limit={8} />
        </div>
      </AppShell>
    </ProtectedRoute>
  );
}
